import { BarChart3, Clock, FileCheck, FileText, Trophy } from 'lucide-react';

interface StatCardProps {
  icon: typeof Clock;
  accentClass: string;
  iconClass: string;
  label: string;
  value: string;
  subValue?: string;
  progressPercent?: number;
}

function StatCard({
  icon,
  accentClass,
  iconClass,
  label,
  value,
  subValue,
  progressPercent,
}: StatCardProps) {
  const Icon = icon;
  return (
    <div className="bg-stat-card flex w-full min-w-0 overflow-hidden rounded-[14px]">
      {/* Left accent strip */}
      <div className={`w-1 shrink-0 ${accentClass}`} aria-hidden="true" />

      {/* Content */}
      <div className="flex min-w-0 flex-1 items-center gap-3 px-3.5 py-4">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${iconClass}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[10px] font-normal text-white/50">{label}</p>
          <p className="truncate text-[15px] font-bold text-white">{value}</p>
          {subValue ? (
            <p className="truncate text-[10px] font-normal text-white/50">{subValue}</p>
          ) : null}
          {typeof progressPercent === 'number' ? (
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-white/10">
              <div
                className={`h-full ${accentClass}`}
                style={{ width: `${String(Math.min(100, Math.max(0, progressPercent)))}%` }}
                aria-hidden="true"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Discriminated union for the Account Usage card. CAB-369 (frontend-only
 * partial): trial / free users see a quota; paid users see "Unlimited".
 *
 * NOTE: `used` for both kinds is currently the all-time per-profile audio
 * sum from CAB-365 (`sumProfileAudioMinutes`). The full per-billing-period
 * rollup is deferred to a follow-up backend ticket. Per product call,
 * showing a number that may exceed the quota in this release is OK — we'll
 * fix it server-side later.
 */
export type AccountUsage =
  | { kind: 'quota'; used: number; total: number }
  | { kind: 'unlimited'; used: number };

export interface StatsBannerProps {
  /** Hours saved this week, e.g. 12.5 */
  timeSavedHours: number;
  /** Running total of completed charts. */
  chartsCompleted: number;
  /**
   * Top-performer profile name + chart count. Set to null to hide the
   * card (e.g. solo users).
   */
  topPerformer: { displayName: string; chartCount: number } | null;
  /**
   * Most-used template's display name. null/undefined → renders an
   * empty-state value.
   */
  mostUsedTemplate: string | null;
  /** Account usage shape — see AccountUsage. */
  accountUsage: AccountUsage;
}

/**
 * Dashboard stats row. Five cards by default; the Top Performer card is
 * omitted entirely when `topPerformer` is null (CAB-368: solo users see
 * a 4-card row).
 */
export function StatsBanner({
  timeSavedHours,
  chartsCompleted,
  topPerformer,
  mostUsedTemplate,
  accountUsage,
}: StatsBannerProps) {
  // Compute the Account Usage card's display value + progress bar based
  // on the discriminated kind. The bar is intentionally hidden for paid
  // ("unlimited") users — there's no meaningful ratio to draw.
  const usageValue =
    accountUsage.kind === 'unlimited'
      ? `${String(accountUsage.used)} min / Unlimited`
      : `${String(accountUsage.used)} / ${String(accountUsage.total)} min`;
  const usagePercent =
    accountUsage.kind === 'quota' && accountUsage.total > 0
      ? (accountUsage.used / accountUsage.total) * 100
      : undefined;

  // 5 cards normally, 4 when the Top Performer card is hidden (solo users).
  // Grid layout so cards always fill the row at equal width regardless of
  // available space; collapses to 2 columns on narrow screens.
  const columnsClass = topPerformer
    ? 'sm:grid-cols-2 lg:grid-cols-5'
    : 'sm:grid-cols-2 lg:grid-cols-4';

  return (
    <div className={`grid grid-cols-1 gap-2.5 ${columnsClass}`}>
      <StatCard
        icon={Clock}
        accentClass="bg-stat-accent-time"
        iconClass="text-stat-accent-time"
        label="Time Saved This Week"
        value={`${timeSavedHours.toFixed(1)} hrs`}
      />
      <StatCard
        icon={FileCheck}
        accentClass="bg-stat-accent-charts"
        iconClass="text-stat-accent-charts"
        label="Charts Completed"
        value={String(chartsCompleted)}
      />
      {topPerformer ? (
        <StatCard
          icon={Trophy}
          accentClass="bg-stat-accent-top"
          iconClass="text-stat-accent-top"
          label="Top Performer"
          value={topPerformer.displayName}
          subValue={`${String(topPerformer.chartCount)} charts`}
        />
      ) : null}
      <StatCard
        icon={FileText}
        accentClass="bg-stat-accent-template"
        iconClass="text-stat-accent-template"
        label="Most-Used Template"
        value={mostUsedTemplate ?? 'No charts yet'}
      />
      <StatCard
        icon={BarChart3}
        accentClass="bg-stat-accent-usage"
        iconClass="text-stat-accent-usage"
        label="Account Usage"
        value={usageValue}
        progressPercent={usagePercent}
      />
    </div>
  );
}
